// src/hooks/useDailyNormPraise.js
import { useCallback, useState } from "react";
import {
  isDailyNormComplete,
  hasDailyNormPraiseBeenShown,
  markDailyNormPraiseShown,
  clearDailyNormPraiseShown,
} from "../utils/dailyNormPraise";

/**
 * Показ похвалы за выполнение дневной нормы.
 *
 * Хук отвечает за:
 *   - проверку «чьи это часы» (похвала только самому сотруднику);
 *   - проверку достижения нормы через чистую isDailyNormComplete;
 *   - защиту от повторного показа за тот же день;
 *   - сброс отметки, когда часы за день больше не покрывают норму
 *     (после удаления или уменьшения ошибочно внесённой записи);
 *   - состояние открытой модалки.
 *
 * Норму и факт передаёт вызывающий код: он уже посчитал их для
 * предупреждений о превышении нормы, дублировать расчёт не нужно.
 */
export function useDailyNormPraise({ currentUser }) {
  const [praise, setPraise] = useState(null);

  const closePraise = useCallback(() => {
    setPraise(null);
  }, []);

  const maybeShowPraise = useCallback(
    ({ empId, date, fact, norm }) => {
      if (!currentUser || empId !== currentUser.id) return;
      if (!isDailyNormComplete({ fact, norm })) return;
      if (hasDailyNormPraiseBeenShown(empId, date)) return;

      markDailyNormPraiseShown(empId, date);
      setPraise({ empId, date });
    },
    [currentUser],
  );

  /**
   * Снять отметку о показе похвалы за день.
   *
   * Вызывается, когда после удаления или уменьшения записи часов факт
   * за день перестал покрывать норму. Следующее достижение нормы за
   * этот день снова покажет модалку.
   */
  const resetPraiseShown = useCallback(({ empId, date }) => {
    clearDailyNormPraiseShown(empId, date);
  }, []);

  return {
    praise,
    closePraise,
    maybeShowPraise,
    resetPraiseShown,
  };
}
